import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import AttendanceStudentsDialog from "./AttendanceStudentsDialog";
import { DeleteForever } from "@mui/icons-material";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import CustomDateRangePicker from "../../Components/Card/DatePicker/CustomDateRangePicker";

import CheckAndReturn from "../../utility/CheckAndReturn";
import ImagePreviewDialog from "../../Components/ImagePreviewDialog/ImagePreviewDialog";
const AttendanceTable = ({ fetchTableTrue, handleEdit }) => {
  const { company_id, permissions } = useSelector(
    (state) => state.GlobalVariables
  );
  const [attendance, setAttendance] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-indexed for MUI
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState("batch_title");
  // 👇 Default to today's date
  const [selectedDate, setSelectedDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [dateRange, setDateRange] = useState([null, null]);

  const [searchValue, setSearchValue] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const fetchAttendance = async (pageNumber = 1, perPage = 10) => {
    try {
      setLoading(true);

      const dataToSend = {
        company_id,
        page: pageNumber,
        per_page: perPage,
        // date: selectedDate,
        searchType: searchType,
        searchValue: searchValue,
        from_date: dateRange[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : "",
        to_date: dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : "",
      };

      const result = await configServ.getAllAttendances(dataToSend);

      if (result?.status === true) {
        setAttendance(result.data);
        setTotal(result.total);
      } else {
        setAttendance([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      setAttendance([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (company_id) fetchAttendance(1, rowsPerPage);
  }, [company_id, rowsPerPage, selectedDate, searchValue, dateRange]);

  useEffect(() => {
    // alert("table")
    fetchAttendance(1, rowsPerPage);
  }, [fetchTableTrue]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchAttendance(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchAttendance(1, newRowsPerPage);
  };

  const handleDelete = async (row) => {
    // 🔹 Step 1: Ask for confirmation
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this attendance record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        // 🔹 Step 2: Call delete API
        const response = await configServ.deleteAttendance({
          attendance_id: row.id,
        });

        if (response.status) {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: response.message || "Attendance deleted successfully.",
            timer: 1500,
            showConfirmButton: false,
          });
          fetchAttendance();
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed!",
            text: response.message || "Unable to delete attendance.",
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message || "Something went wrong while deleting.",
        });
      }
    }
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <>
      <AttendanceStudentsDialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        attendanceId={selectedRow?.id}
        batchId={selectedRow?.batch_id}
      />
      <ImagePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        imageBase64={selectedImage}
        title="Student Photo Preview"
      />

      <Box p={2} width="100%">
        {/* Header */}
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          {/* ✅ Left: Total Records */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" fontWeight="bold">
              Total Records: {total}
            </Typography>
          </Grid>

          {/* ✅ Right: Filters */}
          <Grid
            item
            xs={12}
            md={9}
            container
            spacing={2}
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
          >
            <Grid item xs={12} sm={4} md={3}>
              <CustomDateRangePicker
                value={dateRange}
                onChange={setDateRange}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Search Type</InputLabel>
                <Select
                  value={searchType}
                  label="Search Type"
                  onChange={(e) => setSearchType(e.target.value)}
                >
                  <MenuItem value="batch_title">Batch Title</MenuItem>
                  {/* <MenuItem value="remarks">Remarks</MenuItem>
                  <MenuItem value="date">Date</MenuItem> */}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                onClick={() => {
                  setSelectedDate("");
                  setSearchType("batch_title");
                  setSearchValue("");
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={3}
            >
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              {/* ✅ Desktop / Tablet Table View */}
              <TableContainer
                sx={{ display: { xs: "none", sm: "none", md: "block" } }}
              >
                <Table>
                  <TableHead sx={{ backgroundColor: "#070707ff" }}>
                    <TableRow>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        S.No.
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Batch Title
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Date
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Remarks
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Image
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attendance.length > 0 ? (
                      attendance.map((row, index) => {
                        // 👇 define these INSIDE map
                        const canEdit = CheckAndReturn.PermissionHandler(
                          "edit-attendance",
                          permissions
                        );
                        const canDelete = CheckAndReturn.PermissionHandler(
                          "delete-attendance",
                          permissions
                        );
                        const isToday =
                          dayjs(row.date).format("YYYY-MM-DD") ===
                          dayjs().format("YYYY-MM-DD");

                        return (
                          <TableRow key={row.id} hover>
                            <TableCell>
                              {page * rowsPerPage + index + 1}
                            </TableCell>
                            <TableCell>{row.batch_title || "-"}</TableCell>
                            <TableCell>{row.date}</TableCell>
                            <TableCell>{row.batch_remarks || "-"}</TableCell>

                            <TableCell
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {row.batch_image && (
                                <img
                                  src={row.batch_image}
                                  alt="Batch"
                                  style={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    border: "1px solid #ddd",
                                       cursor: "pointer",   // <-- ADD THIS
                                  }}
                                  onClick={() => {
                                    setSelectedImage(row.batch_image);
                                    setPreviewOpen(true);
                                  }}
                                />
                              )}
                            </TableCell>

                            <TableCell align="center">
                              {/* 👁 View Button */}
                              <IconButton
                                color="primary"
                                onClick={() => {
                                  setSelectedRow(row);
                                  setOpenDialog(true);
                                }}
                                title="View Attendance"
                              >
                                <VisibilityIcon />
                              </IconButton>

                              {/* ✏️ Edit Button with permission + date restriction */}
                              <IconButton
                                color="secondary"
                                onClick={() => handleEdit(row)}
                                disabled={!canEdit || !isToday}
                                title={
                                  !canEdit
                                    ? "You don't have permission to edit attendance"
                                    : !isToday
                                    ? "Editing disabled for past/future dates"
                                    : "Edit"
                                }
                              >
                                <EditIcon
                                  color={
                                    !canEdit || !isToday
                                      ? "disabled"
                                      : "primary"
                                  }
                                />
                              </IconButton>

                              {/* 🗑 Delete Button */}
                              <IconButton
                                color="error"
                                onClick={
                                  canDelete
                                    ? () => handleDelete(row)
                                    : undefined
                                }
                                title="Delete Attendance"
                                disabled={!canDelete}
                              >
                                <DeleteForever disabled={!canDelete} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No attendance records found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* ✅ Mobile Card View */}
              <Box
                sx={{ display: { xs: "block", sm: "block", md: "none" }, p: 1 }}
              >
                {attendance.length > 0 ? (
                  attendance.map((row, index) => {
                    // ✅ Define inside map for each row
                    const canEdit = CheckAndReturn.PermissionHandler(
                      "edit-attendance",
                      permissions
                    );
                    const canDelete = CheckAndReturn.PermissionHandler(
                      "delete-attendance",
                      permissions
                    );
                    const isToday =
                      dayjs(row.date).format("YYYY-MM-DD") ===
                      dayjs().format("YYYY-MM-DD");

                    return (
                      <Paper
                        key={row.id}
                        sx={{
                          p: 2,
                          mb: 1.5,
                          borderRadius: 2,
                          boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Sr.No. {page * rowsPerPage + index + 1} —{" "}
                              {row.batch_title || "-"}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Date: {row.date}
                            </Typography>
                            <Typography variant="body2">
                              Remarks: {row.batch_remarks || "-"}
                            </Typography>
                          </Box>

                          {row.batch_image && (
                            <img
                              src={row.batch_image}
                              alt="Batch"
                              style={{
                                width: 50,
                                height: 50,
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "1px solid #ddd",
                                cursor: "pointer", // <-- ADD THIS
                              }}
                              onClick={() => {
                                setSelectedImage(row.batch_image);
                                setPreviewOpen(true);
                              }}
                            />
                          )}
                        </Box>

                        {/* ✅ Action Buttons */}
                        <Box display="flex" justifyContent="flex-end" mt={1}>
                          {/* 👁 View Button */}
                          <IconButton
                            color="primary"
                            onClick={() => {
                              setSelectedRow(row);
                              setOpenDialog(true);
                            }}
                            title="View Attendance"
                          >
                            <VisibilityIcon />
                          </IconButton>

                          {/* ✏️ Edit Button */}
                          <IconButton
                            color="secondary"
                            onClick={() => handleEdit(row)}
                            disabled={!canEdit || !isToday}
                            title={
                              !canEdit
                                ? "You don't have permission to edit attendance"
                                : !isToday
                                ? "Editing disabled for past/future dates"
                                : "Edit"
                            }
                          >
                            <EditIcon
                              color={
                                !canEdit || !isToday ? "disabled" : "primary"
                              }
                            />
                          </IconButton>

                          {/* 🗑 Delete Button */}
                          <IconButton
                            color="error"
                            onClick={
                              canDelete ? () => handleDelete(row) : undefined
                            }
                            disabled={!canDelete}
                            title="Delete Attendance"
                          >
                            <DeleteForever disabled={!canDelete} />
                          </IconButton>
                        </Box>
                      </Paper>
                    );
                  })
                ) : (
                  <Typography align="center" color="text.secondary">
                    No attendance records found.
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* Pagination */}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 100, 150, 200]}
          />
        </Paper>
      </Box>
    </>
  );
};

export default AttendanceTable;
